import * as Slider from "@radix-ui/react-slider";
import { useId } from "react";
import { useTranslation } from "react-i18next";

interface Mark {
  value: number;
  label: string;
}

interface SliderWithLabelProps {
  label: string;
  min: number;
  max: number;
  value: number;
  marks?: Mark[];
  step?: number;
  onChange: (value: number) => void;
  padding?: string; // Optional padding prop
  disabled?: boolean;
}

/**
 * SliderWithLabel — A fully controlled slider component with a label and current value display.
 *
 * Example usage:
 *
 * const [value, setValue] = useState(2048);
 *
 * <SliderWithLabel
 *   label="Context Length"
 *   min={0}
 *   max={4096}
 *   step={256}
 *   value={value}
 *   marks={[
 *     { value: 0, label: "0" },
 *     { value: 2048, label: "2048" },
 *     { value: 4096, label: "4096" },
 *   ]}
 *   onChange={setValue}
 * />
 */
export const SliderWithLabel = ({
  label,
  min,
  max,
  step = 1,
  marks,
  value,
  onChange,
  padding = "4px 8px 8px 8px", // Default padding if not provided
  disabled = false,
}: SliderWithLabelProps) => {
  const labelId = useId();
  const { i18n } = useTranslation();
  const dir = i18n.dir();
  // Match MUI's bounded thumb presentation without changing the caller's form
  // value when a selected model supplies a different token limit.
  const boundedValue = Math.min(max, Math.max(min, value));
  const markPosition = (mark: Mark) =>
    max === min ? 0 : ((mark.value - min) / (max - min)) * 100;

  return (
    <div className="flex flex-1 flex-col" style={{ padding }} dir={dir}>
      {/* Header: Label + Current Value */}
      <div className="mb-1 flex justify-between gap-2 text-sm">
        <span id={labelId} className="text-muted-foreground">
          {label}
        </span>
        <span className="font-medium text-foreground">
          {value}
        </span>
      </div>

      {/* Slider */}
      <Slider.Root
        className="relative flex h-[30px] w-full touch-none select-none items-center data-[disabled]:opacity-50"
        dir={dir}
        value={[boundedValue]}
        onValueChange={([next]) => {
          if (next !== undefined) onChange(next);
        }}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
      >
        <Slider.Track className="relative h-1 w-full grow rounded-full bg-primary/30">
          <Slider.Range className="absolute h-full rounded-full bg-primary" />
        </Slider.Track>
        {marks?.map((mark) => (
          <span
            key={mark.value}
            aria-hidden="true"
            className="pointer-events-none absolute h-1 w-0.5 bg-background/70"
            style={{ insetInlineStart: `${markPosition(mark)}%` }}
          />
        ))}
        <Slider.Thumb
          aria-labelledby={labelId}
          className="group relative block size-5 rounded-full border border-primary bg-primary shadow-sm outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none"
        >
          <span aria-hidden="true" className="pointer-events-none absolute -top-8 start-1/2 -translate-x-1/2 rounded bg-popover px-2 py-1 text-xs text-popover-foreground opacity-0 shadow-sm group-hover:opacity-100 group-focus-visible:opacity-100 rtl:translate-x-1/2">
            {boundedValue}
          </span>
        </Slider.Thumb>
      </Slider.Root>
      {marks?.length ? (
        <div className="relative h-6 text-xs text-muted-foreground">
          {marks.map((mark) => (
            <span key={mark.value} className="absolute -translate-x-1/2 whitespace-nowrap rtl:translate-x-1/2" style={{ insetInlineStart: `${markPosition(mark)}%` }}>
              {mark.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};
